/**
 * 14 #9 — Board view of the Blocked tab: one column per reason (Reason missing, Site Team, Awaiting drawings,
 * Pending coordination, Missing design info, Consultant comments …) plus "Unblock → In progress". Moving a card
 * (drag, keyboard or its "Move to…" menu — the foundation Board) sets the reason; dropping it on Unblock moves the
 * drawing to In progress. Cards keep the list's order (longest blocked first).
 * Without onSetReason / onUnblock (BUG-002: Shop Drawings is View only for the role, or a Viewer account) the board
 * is read-only: no drag, no "Move to…" menu and no Unblock column (the foundation Board without onMove).
 */
import { useMemo } from 'react';
import { Board } from '@/components/ui/board';
import { cn } from '@/lib/utils';
import {
  BLOCKER_HINTS,
  BLOCKER_LABELS,
  BLOCKER_REASONS,
  blockedSince,
  blockedSinceText,
  blockerMeta,
  displayPerson,
  drawingTitle,
  labelize,
  projectNumber,
} from './model';
import { AgeText, DisciplineChip, DrawingIdText, PersonAvatar } from './SdUi';

export const UNBLOCK_COLUMN = '__unblock__';
// Shown even when empty (the reasons people actually use); the rest appear when a drawing has them.
const MAIN_REASONS = ['SITE_TEAM', 'AWAITING_DRAWINGS', 'PENDING_COORDINATION', 'MISSING_DESIGN_INFO', 'CONSULTANT_COMMENTS'];

function BlockedCard({ drawing, busy }) {
  const since = blockedSince(drawing);
  const owner = displayPerson(drawing.resp);
  const number = projectNumber(drawing);
  return (
    <div className={cn('space-y-1.5', busy && 'opacity-60')}>
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 text-[13px] font-semibold leading-[17px] text-ink">{drawingTitle(drawing)}</span>
          <span className="mt-0.5 block truncate text-[11.5px] text-ink-3">
            <b className="font-semibold text-ink-2">{drawing.project || 'No project'}</b>{number ? <span className="font-mono"> #{number}</span> : null}
            {drawing.floor && drawing.floor !== drawingTitle(drawing) ? ` · ${drawing.floor}` : ''}
          </span>
        </span>
        <DisciplineChip disc={drawing.disc} className="h-[18px] min-w-[32px] rounded-[5px] px-1 text-[9.5px]" />
      </div>
      <div className="flex items-center gap-2 text-[11.5px]">
        <DrawingIdText value={drawing.drawingId} className="min-w-0 shrink" />
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {owner ? <span title={`Owner: ${owner}`} className="inline-flex"><PersonAvatar name={owner} size={20} /><span className="sr-only">Owner {owner}</span></span> : <span className="italic text-ink-3">no owner</span>}
          {since ? <span title={blockedSinceText(since).title}><AgeText days={since.days} className="text-[12px]" /></span> : null}
        </span>
      </div>
    </div>
  );
}

export function BlockedBoard({ rows, onSetReason, onUnblock, onOpen }) {
  const readOnly = !onSetReason || !onUnblock;
  const columns = useMemo(() => {
    const counts = new Map();
    rows.forEach((drawing) => counts.set(blockerMeta(drawing).key, (counts.get(blockerMeta(drawing).key) || 0) + 1));
    // The reasons in use first (most drawings first), then the usual ones, so the board reads like the Pareto chips.
    const rank = (reason) => (MAIN_REASONS.includes(reason) ? MAIN_REASONS.indexOf(reason) : MAIN_REASONS.length);
    const reasons = BLOCKER_REASONS
      .filter((reason) => MAIN_REASONS.includes(reason) || counts.has(reason))
      .sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0) || rank(a) - rank(b));
    return [
      { key: 'REASON_MISSING', label: 'Reason missing', tone: 'amber', hint: 'Nobody knows who has to act' },
      ...reasons.map((reason) => ({
        key: reason,
        label: labelize(reason, BLOCKER_LABELS),
        tone: reason === 'CONSULTANT_COMMENTS' ? 'amber' : 'red',
        hint: BLOCKER_HINTS[reason] ? `Consultant ${BLOCKER_HINTS[reason]}` : undefined,
      })),
      readOnly ? null : { key: UNBLOCK_COLUMN, label: 'Unblock → In progress', tone: 'blue', hint: 'Drop a card here when it can move on', count: 0 },
    ].filter(Boolean);
  }, [readOnly, rows]);

  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface-2 p-3" data-testid="blocked-board">
      <Board
        ariaLabel="Blocked drawings by reason"
        columns={columns}
        items={rows}
        getKey={(drawing) => drawing.id}
        getColumn={(drawing) => blockerMeta(drawing).key}
        getCardLabel={(drawing) => `${drawingTitle(drawing)} (${drawing.project || 'no project'})`}
        renderCard={(drawing, { busy }) => <BlockedCard drawing={drawing} busy={busy} />}
        onMove={readOnly ? undefined : async (drawing, to) => {
          if (to === UNBLOCK_COLUMN) await onUnblock(drawing);
          else await onSetReason(drawing, to === 'REASON_MISSING' ? 'NONE' : to);
        }}
        onOpen={onOpen}
        columnWidth={248}
        columnLimit={30}
        emptyText="No drawings"
        testId="blocked-board-columns"
      />
    </div>
  );
}
